import { StatusTreeSelect } from '../components/StatusTreeSelect';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuthStore } from '../store/auth';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { createPortal } from 'react-dom';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  Zap,
  Play,
  Pause,
  Edit2,
  Trash2,
  Copy,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  History,
  MoreVertical,
  Search,
  RefreshCw,
  Activity,
  Settings2,
  ArrowRight,
  Package,
  Truck,
  FileText,
  Save,
  X,
  Mail,
  Tag,
  Webhook,
  Printer,
  Timer,
  Info,
  HelpCircle,
  GripVertical,
  ChevronDown,
  ChevronRight,
  FolderPlus,
  Folder,
  Download,
  Upload,
  Check,
} from 'lucide-react';
import {
  automationApi,
  type Automation,
  type AutomationLog,
  type AutomationGroup,
  type TriggerMeta,
  type ActionMeta,
  type ConditionMeta,
  type TriggerType,
  type ActionType,
  type TriggerMode,
} from '../lib/api';
import * as LucideIcons from 'lucide-react';
import api from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { GroupedSelect } from '../components/ui/GroupedSelect';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';

// Trigger icons
const triggerIcons: Record<string, React.ElementType> = {
  'order.created': Package,
  'order.status_changed': RefreshCw,
  'order.updated': Edit2,
  'shipment.created': Truck,
  'shipment.status_changed': Activity,
  'shipment.validation_error': AlertCircle,
  'shipment.label_printed': Printer,
  'invoice.created': FileText,
};

// Trigger type to Polish name mapping
const triggerNames: Record<string, string> = {
  'order.created': 'Nowe zamówienie',
  'order.status_changed': 'Zmiana statusu zamówienia',
  'order.updated': 'Aktualizacja zamówienia',
  'shipment.created': 'Utworzono przesyłkę',
  'shipment.status_changed': 'Zmiana statusu przesyłki',
  'shipment.validation_error': 'Błąd walidacji przesyłki',
  'shipment.label_printed': 'Pobranie listu przewozowego',
  'invoice.created': 'Wystawiono fakturę',
  'schedule.daily': 'Codziennie o określonej godzinie',
  'schedule.hourly': 'Co X godzin',
  'order.in_status_duration': 'Zamówienie w statusie > X dni',
  'manual.trigger': 'Ręczne wywołanie akcji',
};

// Action icons
const actionIcons: Record<string, React.ElementType> = {
  change_status: RefreshCw,
  sync_status_woo: RefreshCw,
  create_shipment: Truck,
  send_email: Mail,
  create_invoice: FileText,
  create_correction: FileText,
  create_refund: RefreshCw,
  add_note: Edit2,
  add_tag: Tag,
  remove_tag: Tag,
  webhook: Webhook,
  delay: Timer,
};

// Category colors
const categoryColors: Record<string, string> = {
  order: 'neon-cyan',
  orders: 'neon-cyan',
  shipment: 'neon-purple',
  shipments: 'neon-purple',
  invoice: 'neon-green',
  invoices: 'neon-green',
  schedule: 'neon-orange',
};

// Operator labels in Polish
const operatorLabels: Record<string, string> = {
  equals: 'równa się',
  not_equals: 'nie równa się',
  contains: 'zawiera',
  not_contains: 'nie zawiera',
  in: 'jest w liście',
  not_in: 'nie jest w liście',
  greater_than: 'większe niż',
  less_than: 'mniejsze niż',
  between: 'pomiędzy',
  is_empty: 'jest puste',
  is_not_empty: 'nie jest puste',
  has_tag: 'ma tag',
  not_has_tag: 'nie ma tagu',
  true: 'TAK (jest)',
  false: 'NIE (brak)',
};

// Types for condition groups with AND/OR logic
interface SimpleCondition {
  field: string;
  operator: string;
  value: unknown;
}

interface ConditionGroup {
  logic: 'AND' | 'OR';
  conditions: (SimpleCondition | ConditionGroup)[];
}

type ConditionItem = SimpleCondition | ConditionGroup;

// Helper to check if item is a group
const isConditionGroup = (item: ConditionItem): item is ConditionGroup => {
  return 'logic' in item && 'conditions' in item;
};

// Helper function to get operator label
const getOperatorLabel = (operator: string): string => {
  return operatorLabels[operator] || operator.replace(/_/g, ' ');
};

export function AutomationsPage() {
  return (
    <Routes>
      <Route index element={<AutomationsList />} />
      <Route path="new" element={<AutomationBuilder />} />
      <Route path=":id" element={<AutomationBuilder />} />
      <Route path=":id/logs" element={<AutomationLogs />} />
      <Route path="logs" element={<AllLogsPage />} />
    </Routes>
  );
}

function AutomationsList() {
  const userId = useAuthStore(state => state.user?.id);
  const tenantId = useAuthStore(state => state.tenant?.id);
  const sessionKey = `ecomflow_automation_expansion:${tenantId}:${userId}`;
  return <AutomationsListContent key={sessionKey} sessionKey={sessionKey} />;
}

function AutomationsListContent({ sessionKey }: { sessionKey: string }) {
  const { showSuccess, showError, showInfo } = useNotification();
  const confirmDialog = useConfirmDialog();
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [groups, setGroups] = useState<AutomationGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const saved: unknown = JSON.parse(sessionStorage.getItem(sessionKey) || '[]');
      return new Set(Array.isArray(saved) ? saved.filter((id): id is string => typeof id === 'string') : []);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(sessionKey, JSON.stringify([...expandedGroups]));
    } catch {
      // Keep in-memory expansion working when browser storage is unavailable.
    }
  }, [sessionKey, expandedGroups]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<AutomationGroup | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#6B7280');
  const [importing, setImporting] = useState(false);
  const [deleteGroupConfirm, setDeleteGroupConfirm] = useState<{ show: boolean; groupId: string | null; groupName: string }>({ show: false, groupId: null, groupName: '' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Handle ESC key to close group modal
  const closeGroupModal = useCallback(() => setShowGroupModal(false), []);
  useEscapeKey(closeGroupModal, showGroupModal);

  // Handle ESC key to close delete group confirmation
  const closeDeleteGroupConfirm = useCallback(() => setDeleteGroupConfirm({ show: false, groupId: null, groupName: '' }), []);
  useEscapeKey(closeDeleteGroupConfirm, deleteGroupConfirm.show);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [automationsRes, groupsRes] = await Promise.all([
        automationApi.getAll(),
        automationApi.getGroups(),
      ]);
      setAutomations(automationsRes.data.automations);
      setGroups(groupsRes.data.groups);
    } catch (err) {
      setError('Nie udało się załadować danych');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await automationApi.toggle(id);
      loadData();
    } catch (err) {
      showError(err, 'automation');
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirmDialog({
      title: 'Usuń automatyzację',
      message: 'Czy na pewno chcesz usunąć tę automatyzację?',
      confirmLabel: 'Usuń',
    }))) return;
    try {
      await automationApi.delete(id);
      loadData();
    } catch (err) {
      showError(err, 'automation');
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await automationApi.duplicate(id);
      loadData();
      showSuccess('duplicate', { message: 'Automatyzacja skopiowana.' });
    } catch (err) {
      showError(err, 'automation');
    }
  };

  const handleClearError = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Don't navigate to edit page
    try {
      await automationApi.clearError(id);
      loadData();
      showInfo('Błąd wyczyszczony', 'Status błędu automatyzacji został wyczyszczony');
    } catch (err) {
      showError(err, 'automation');
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      await automationApi.createGroup({ name: newGroupName, color: newGroupColor });
      setShowGroupModal(false);
      setNewGroupName('');
      setNewGroupColor('#6B7280');
      loadData();
    } catch (err) {
      showError(err, 'group');
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !newGroupName.trim()) return;
    try {
      await automationApi.updateGroup(editingGroup.id, { name: newGroupName, color: newGroupColor });
      setEditingGroup(null);
      setShowGroupModal(false);
      setNewGroupName('');
      setNewGroupColor('#6B7280');
      loadData();
    } catch (err) {
      showError(err, 'group');
    }
  };

  const handleDeleteGroup = (id: string) => {
    const group = groups.find(g => g.id === id);
    setDeleteGroupConfirm({ show: true, groupId: id, groupName: group?.name || '' });
  };

  const confirmDeleteGroup = async (deleteAutomations: boolean) => {
    if (!deleteGroupConfirm.groupId) return;
    try {
      await automationApi.deleteGroup(deleteGroupConfirm.groupId, deleteAutomations);
      loadData();
      showSuccess('delete', { message: deleteAutomations ? 'Grupa i automatyzacje usunięte' : 'Grupa usunięta' });
    } catch (err) {
      showError(err, 'group');
    } finally {
      setDeleteGroupConfirm({ show: false, groupId: null, groupName: '' });
    }
  };

  const handleDuplicateGroup = async (id: string) => {
    try {
      const { data } = await automationApi.duplicateGroup(id);
      loadData();
      showSuccess('duplicate', { message: data.message });
    } catch (err) {
      showError(err, 'group');
    }
  };

  const handleExport = async () => {
    try {
      const { data } = await automationApi.exportAll();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `automatyzacje-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      showError(err, 'export');
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const { data: result } = await automationApi.importAll(data);
      loadData();
      showInfo('Import zakończony', result.message, result.note);
    } catch (err) {
      showError('Nie udało się zaimportować automatyzacji. Sprawdź format pliku.', 'import');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const toggleGroupExpand = (groupId: string) => {
    setExpandedGroups(previous => {
      const next = new Set(previous);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = automations.findIndex(a => a.id === active.id);
    const newIndex = automations.findIndex(a => a.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newAutomations = arrayMove(automations, oldIndex, newIndex);
    setAutomations(newAutomations);

    // Update positions on server
    const items = newAutomations.map((a, index) => ({
      id: a.id,
      position: index,
      groupId: a.groupId || null,
    }));

    try {
      await automationApi.reorder(items);
    } catch (err) {
      // Revert on error
      loadData();
    }
  };

  // Filter automations
  const filteredAutomations = automations.filter((a) => {
    const matchesSearch =
      a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter =
      filterActive === 'all' ||
      (filterActive === 'active' && a.isActive) ||
      (filterActive === 'inactive' && !a.isActive);
    return matchesSearch && matchesFilter;
  });

  // Group automations by groupId
  const automationsByGroup = useMemo(() => {
    const result: Record<string, Automation[]> = { ungrouped: [] };

    groups.forEach(g => {
      result[g.id] = [];
    });

    filteredAutomations.forEach(a => {
      if (a.groupId && result[a.groupId]) {
        result[a.groupId].push(a);
      } else {
        result.ungrouped.push(a);
      }
    });

    return result;
  }, [filteredAutomations, groups]);

  const groupColors = [
    '#EF4444', '#F97316', '#F59E0B', '#84CC16', '#22C55E',
    '#14B8A6', '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6',
    '#A855F7', '#D946EF', '#EC4899', '#6B7280',
  ];

  return (
    <div className="space-y-8">
      <div className="animate-fade-in">
        <PageHeader
          icon={Zap}
          title="Automatyzacje"
          subtitle="Automatyzuj procesy obsługi zamówień, wysyłek i faktur"
          actions={
            <>
              <Button
                variant="secondary"
                onClick={handleExport}
                title="Eksportuj wszystkie automatyzacje do pliku JSON"
                leftIcon={<Download />}
              >
                Eksport
              </Button>
              <label
                className="inline-flex items-center gap-2 px-field-x py-field-y rounded-btn text-sm font-semibold bg-surface-hover/60 text-text-primary border border-accent-primary/30 hover:border-accent-primary/50 hover:bg-surface-muted/60 backdrop-blur-sm cursor-pointer transition-colors"
                title="Importuj automatyzacje z pliku JSON"
              >
                <span className="inline-flex items-center justify-center w-4 h-4 [&>svg]:w-full [&>svg]:h-full">
                  <Upload />
                </span>
                {importing ? 'Importowanie...' : 'Import'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  className="hidden"
                  disabled={importing}
                />
              </label>
              <Button
                variant="secondary"
                onClick={() => {
                  setEditingGroup(null);
                  setNewGroupName('');
                  setNewGroupColor('#6B7280');
                  setShowGroupModal(true);
                }}
                leftIcon={<FolderPlus />}
              >
                Nowa grupa
              </Button>
              <Link
                to="/automations/logs"
                className="inline-flex items-center gap-2 px-field-x py-field-y rounded-btn text-sm font-semibold bg-surface-hover/60 text-text-primary border border-accent-primary/30 hover:border-accent-primary/50 hover:bg-surface-muted/60 backdrop-blur-sm transition-colors"
              >
                <History className="w-4 h-4" />
                Logi
              </Link>
              <Link
                to="/automations/new"
                className="inline-flex items-center gap-2 px-field-x py-field-y rounded-btn text-sm font-semibold bg-accent-primary text-white hover:bg-accent-info transition-colors"
              >
                <Plus className="w-4 h-4" />
                Nowa automatyzacja
              </Link>
            </>
          }
        />
      </div>

      {error && (
        <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger animate-scale-in">
          <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-slide-up">
        <div className="card bg-gradient-to-br from-accent-primary/10 to-transparent border-accent-primary/20">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-accent-primary/20">
              <Zap className="w-6 h-6 text-accent-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">{automations.length}</p>
              <p className="text-sm text-text-secondary">Wszystkie</p>
            </div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-success/10 to-transparent border-success/20">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-success/20">
              <Play className="w-6 h-6 text-success" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">
                {automations.filter((a) => a.isActive).length}
              </p>
              <p className="text-sm text-text-secondary">Aktywne</p>
            </div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-gray-500/10 to-transparent border-gray-500/20">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gray-500/20">
              <Pause className="w-6 h-6 text-text-secondary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">
                {automations.filter((a) => !a.isActive).length}
              </p>
              <p className="text-sm text-text-secondary">Wyłączone</p>
            </div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-accent-secondary/10 to-transparent border-accent-secondary/20">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-accent-secondary/20">
              <Activity className="w-6 h-6 text-accent-secondary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">
                {automations.reduce((sum, a) => sum + a.runCount, 0)}
              </p>
              <p className="text-sm text-text-secondary">Wykonań</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 animate-slide-up" style={{ animationDelay: '0.1s' }}>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
          <input
            type="text"
            placeholder="Szukaj automatyzacji..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFilterActive('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filterActive === 'all'
                ? 'bg-surface-hover text-text-primary'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Wszystkie
          </button>
          <button
            onClick={() => setFilterActive('active')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filterActive === 'active'
                ? 'bg-success/20 text-success'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Aktywne
          </button>
          <button
            onClick={() => setFilterActive('inactive')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filterActive === 'inactive'
                ? 'bg-gray-500/20 text-text-secondary'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Wyłączone
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="loader" />
        </div>
      ) : filteredAutomations.length === 0 ? (
        <div className="animate-slide-up">
          <EmptyState
            icon={Zap}
            title={searchQuery || filterActive !== 'all' ? 'Brak wyników' : 'Brak automatyzacji'}
            description={searchQuery || filterActive !== 'all' ? 'Spróbuj zmienić kryteria wyszukiwania' : 'Utwórz pierwszą automatyzację, aby usprawnić obsługę zamówień.'}
            buttonLabel={!searchQuery && filterActive === 'all' ? 'Utwórz automatyzację' : undefined}
            buttonTo={!searchQuery && filterActive === 'all' ? '/automations/new' : undefined}
            buttonIcon={!searchQuery && filterActive === 'all' ? Plus : undefined}
          />
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="space-y-6 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            {/* Groups */}
            {groups.map((group) => {
              const groupAutomations = automationsByGroup[group.id] || [];
              const isExpanded = expandedGroups.has(group.id);

              return (
                <div key={group.id} className="card !p-0 overflow-hidden">
                  {/* Group header */}
                  <div
                    className="flex items-center gap-3 p-4 bg-surface-hover/50 cursor-pointer hover:bg-surface-hover"
                    onClick={() => toggleGroupExpand(group.id)}
                  >
                    <button className="p-1 text-text-secondary">
                      {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </button>
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: group.color }}
                    />
                    <Folder className="w-5 h-5 text-text-secondary" />
                    <span className="font-medium text-text-primary flex-1">{group.name}</span>
                    <span className="text-sm text-text-muted">{groupAutomations.length}</span>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleDuplicateGroup(group.id)}
                        className="p-2 text-text-secondary hover:text-success hover:bg-success/10 rounded-lg transition-all"
                        title="Kopiuj grupę z automatyzacjami"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingGroup(group);
                          setNewGroupName(group.name);
                          setNewGroupColor(group.color);
                          setShowGroupModal(true);
                        }}
                        className="p-2 text-text-secondary hover:text-accent-primary hover:bg-accent-primary/10 rounded-lg transition-all"
                        title="Edytuj grupę"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteGroup(group.id)}
                        className="p-2 text-text-secondary hover:text-danger hover:bg-danger/10 rounded-lg transition-all"
                        title="Usuń grupę"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Group content */}
                  {isExpanded && (
                    <div className="p-3 space-y-2">
                      {groupAutomations.length === 0 ? (
                        <EmptyState
                          icon={Zap}
                          title="Brak automatyzacji w tej grupie"
                          size="sm"
                        />
                      ) : (
                        <SortableContext
                          items={groupAutomations.map(a => a.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {groupAutomations.map((automation) => (
                            <SortableAutomationCard
                              key={automation.id}
                              automation={automation}
                              onToggle={handleToggle}
                              onDelete={handleDelete}
                              onDuplicate={handleDuplicate}
                              onClearError={handleClearError}
                              onViewLogs={() => navigate(`/automations/${automation.id}/logs`)}
                            />
                          ))}
                        </SortableContext>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Ungrouped automations */}
            {automationsByGroup.ungrouped.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-gray-500" />
                  Bez grupy
                  <span className="text-sm text-text-muted">({automationsByGroup.ungrouped.length})</span>
                </h2>
                <SortableContext
                  items={automationsByGroup.ungrouped.map(a => a.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {automationsByGroup.ungrouped.map((automation) => (
                      <SortableAutomationCard
                        key={automation.id}
                        automation={automation}
                        onToggle={handleToggle}
                        onDelete={handleDelete}
                        onDuplicate={handleDuplicate}
                        onClearError={handleClearError}
                        onViewLogs={() => navigate(`/automations/${automation.id}/logs`)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </div>
            )}
          </div>
        </DndContext>
      )}

      {/* Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowGroupModal(false)} />
          <div className="relative bg-surface-raised border border-border rounded-xl p-6 w-full max-w-md animate-scale-in">
            <h3 className="text-xl font-semibold text-text-primary mb-4">
              {editingGroup ? 'Edytuj grupę' : 'Nowa grupa'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="label">Nazwa grupy</label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  className="input"
                  placeholder="np. Obsługa zamówień"
                  autoFocus
                />
              </div>
              <div>
                <label className="label">Kolor</label>
                <div className="flex flex-wrap gap-2">
                  {groupColors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setNewGroupColor(color)}
                      className={`w-8 h-8 rounded-lg transition-all ${
                        newGroupColor === color
                          ? 'ring-2 ring-white ring-offset-2 ring-offset-dark-800'
                          : 'hover:scale-110'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-6">
              <Button
                variant="primary"
                onClick={editingGroup ? handleUpdateGroup : handleCreateGroup}
                disabled={!newGroupName.trim()}
              >
                {editingGroup ? 'Zapisz' : 'Utwórz'}
              </Button>
              <Button variant="secondary" onClick={() => setShowGroupModal(false)}>
                Anuluj
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Group Confirmation Modal */}
      {deleteGroupConfirm.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60" onClick={() => setDeleteGroupConfirm({ show: false, groupId: null, groupName: '' })} />
          <div className="relative bg-surface-raised border border-border rounded-xl p-6 w-full max-w-md animate-scale-in">
            <h3 className="text-xl font-semibold text-text-primary mb-4">
              Usuń grupę „{deleteGroupConfirm.groupName}"
            </h3>
            <p className="text-text-secondary mb-6">
              Czy chcesz również usunąć wszystkie automatyzacje znajdujące się w tej grupie?
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => confirmDeleteGroup(true)}
                className="btn w-full bg-danger/20 hover:bg-danger/30 text-danger border border-danger/30"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Tak, usuń grupę i automatyzacje
              </button>
              <button
                onClick={() => confirmDeleteGroup(false)}
                className="btn w-full bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border border-orange-500/30"
              >
                <Folder className="w-4 h-4 mr-2" />
                Nie, usuń tylko grupę (rozgrupuj automatyzacje)
              </button>
              <button
                onClick={() => setDeleteGroupConfirm({ show: false, groupId: null, groupName: '' })}
                className="btn-secondary w-full"
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Sortable Automation Card wrapper
function SortableAutomationCard(props: {
  automation: Automation;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onClearError: (id: string, e: React.MouseEvent) => void;
  onViewLogs: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.automation.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <AutomationCard {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

function AutomationCard({
  automation,
  onToggle,
  onDelete,
  onDuplicate,
  onClearError,
  onViewLogs,
  dragHandleProps,
}: {
  automation: Automation;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onClearError: (id: string, e: React.MouseEvent) => void;
  onViewLogs: () => void;
  dragHandleProps?: Record<string, unknown>;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Handle both single trigger (legacy) and multiple triggers (new)
  const automationTriggers = Array.isArray(automation.trigger)
    ? automation.trigger
    : [automation.trigger];
  const TriggerIcon = triggerIcons[automationTriggers[0]?.type] || Zap;

  const handleMenuToggle = () => {
    if (!showMenu && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      const menuHeight = 100; // Approximate menu height
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Position above if not enough space below
      const top = spaceBelow < menuHeight && spaceAbove > spaceBelow
        ? rect.top - menuHeight - 4
        : rect.bottom + 4;

      setMenuPosition({
        top,
        left: rect.right - 192, // 192px = w-48
      });
    }
    setShowMenu(!showMenu);
  };

  return (
    <div
      className={`card !p-4 flex items-center gap-4 group transition-all hover:border-border ${
        !automation.isActive ? 'opacity-60' : ''
      }`}
    >
      {/* Drag handle */}
      {dragHandleProps && (
        <button
          {...dragHandleProps}
          className="p-1 text-text-muted hover:text-text-secondary cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="w-5 h-5" />
        </button>
      )}

      {/* Toggle */}
      <button
        onClick={() => onToggle(automation.id)}
        className={`relative w-12 h-7 rounded-full transition-colors ${
          automation.isActive ? 'bg-success' : 'bg-surface-muted'
        }`}
      >
        <span
          className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
            automation.isActive ? 'left-6' : 'left-1'
          }`}
        />
      </button>

      {/* Trigger icon */}
      <div
        className={`p-3 rounded-xl ${
          automation.isActive
            ? `bg-${categoryColors[automation.triggerCategory || 'order']}/20`
            : 'bg-surface-hover'
        }`}
      >
        <TriggerIcon
          className={`w-5 h-5 ${
            automation.isActive
              ? `text-${categoryColors[automation.triggerCategory || 'order']}`
              : 'text-text-muted'
          }`}
        />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-text-primary truncate">{automation.name}</h3>
          {automation.lastError && (
            <span
              className="badge badge-error text-xs cursor-help inline-flex items-center gap-1"
              title={`${automation.lastError}${automation.lastRunAt ? `\n\nOstatnia próba: ${new Date(automation.lastRunAt).toLocaleString('pl-PL')}` : ''}`}
            >
              Błąd
              <button
                onClick={(e) => onClearError(automation.id, e)}
                className="ml-0.5 hover:bg-danger rounded-full p-0.5 transition-colors"
                title="Wyczyść błąd"
              >
                <LucideIcons.X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 mt-1 text-sm text-text-secondary">
          <span className="flex items-center gap-1">
            <TriggerIcon className="w-4 h-4" />
            {automationTriggers.length > 1
              ? `${automationTriggers.length} triggery`
              : (automation.triggerName || triggerNames[automationTriggers[0]?.type] || automationTriggers[0]?.type)}
          </span>
          <span className="flex items-center gap-1">
            <ArrowRight className="w-4 h-4" />
            {automation.actions.length} {automation.actions.length === 1 ? 'akcja' : 'akcji'}
          </span>
          {automation.runCount > 0 && (
            <span className="flex items-center gap-1">
              <Activity className="w-4 h-4" />
              {automation.runCount}x
            </span>
          )}
        </div>
      </div>

      {/* Last run */}
      {automation.lastRunAt && (
        <div className="text-sm text-text-muted hidden md:block">
          <Clock className="w-4 h-4 inline mr-1" />
          {new Date(automation.lastRunAt).toLocaleString('pl-PL')}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={onViewLogs}
          className="p-2 rounded-lg text-text-secondary hover:text-accent-secondary hover:bg-accent-secondary/10 transition-all"
          title="Historia"
        >
          <History className="w-4 h-4" />
        </button>
        <Link
          to={`/automations/${automation.id}`}
          className="p-2 rounded-lg text-text-secondary hover:text-accent-primary hover:bg-accent-primary/10 transition-all"
          title="Edytuj"
          aria-label={`Edytuj automatyzację ${automation.name}`}
        >
          <Edit2 className="w-4 h-4" />
        </Link>
        <div className="relative">
          <button
            ref={menuButtonRef}
            onClick={handleMenuToggle}
            className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {showMenu && createPortal(
            <>
              <div className="fixed inset-0 z-[9998]" onClick={() => setShowMenu(false)} />
              <div
                className="fixed w-48 bg-surface-raised border border-border rounded-xl shadow-xl z-[9999] py-1"
                style={{ top: menuPosition.top, left: menuPosition.left }}
              >
                <button
                  onClick={() => {
                    onDuplicate(automation.id);
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-text-secondary hover:bg-surface-hover hover:text-text-primary flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Duplikuj
                </button>
                <button
                  onClick={() => {
                    onDelete(automation.id);
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-danger hover:bg-danger/10 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Usuń
                </button>
              </div>
            </>,
            document.body
          )}
        </div>
      </div>
    </div>
  );
}

interface Status {
  id: string;
  name: string;
  color: string;
  parentId?: string | null;
  children?: Status[];
}

interface Store {
  id: string;
  name: string;
  platform: string;
}

interface EmailTemplate {
  id: string;
  name: string;
}


function AutomationBuilder() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [priority, setPriority] = useState(0);
  const [groupId, setGroupId] = useState<string | null>(null);

  // Trigger mode state
  const [triggerMode, setTriggerMode] = useState<TriggerMode>('automatic');
  const [buttonIcon, setButtonIcon] = useState<string>('Play');
  const [buttonText, setButtonText] = useState<string>('');
  const [visibleInStores, setVisibleInStores] = useState<string[]>([]);
  const [availableIcons, setAvailableIcons] = useState<string[]>([]);
  const [showIconPicker, setShowIconPicker] = useState(false);

  // Trigger state - supports multiple triggers (OR logic)
  const [selectedTriggers, setSelectedTriggers] = useState<TriggerType[]>([]);
  const [triggerConfigs, setTriggerConfigs] = useState<Record<string, Record<string, unknown>>>({});

  // Conditions state - now supports groups with AND/OR logic
  const [conditions, setConditions] = useState<ConditionItem[]>([]);
  const [conditionsLogic, setConditionsLogic] = useState<'AND' | 'OR'>('AND');

  // Actions state
  const [actions, setActions] = useState<Array<{
    type: ActionType;
    config: Record<string, unknown>;
    continueOnError: boolean;
  }>>([]);

  // Metadata
  const [triggers, setTriggers] = useState<TriggerMeta[]>([]);
  const [actionsMeta, setActionsMeta] = useState<ActionMeta[]>([]);
  const [conditionsMeta, setConditionsMeta] = useState<ConditionMeta[]>([]);
  const [inpostStatuses, setInpostStatuses] = useState<Array<{ value: string; label: string }>>([]);
  const [validationErrors, setValidationErrors] = useState<Array<{ value: string; label: string }>>([]);
  const [orderFields, setOrderFields] = useState<{
    paymentMethods: string[];
    shippingMethods: string[];
    tags: string[];
    paymentMethodsByStore: Array<{ storeId: string | null; storeName: string; methods: string[] }>;
    shippingMethodsByStore: Array<{ storeId: string | null; storeName: string; methods: string[] }>;
  }>({
    paymentMethods: [],
    shippingMethods: [],
    tags: [],
    paymentMethodsByStore: [],
    shippingMethodsByStore: [],
  });

  // Reference data
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [groups, setGroups] = useState<AutomationGroup[]>([]);

  // Check if editing existing automation
  const [automationId, setAutomationId] = useState<string | null>(null);

  useEffect(() => {
    loadMetadata();
  }, []);

  const loadMetadata = async () => {
    try {
      setLoading(true);

      // Load metadata in parallel
      const [triggersRes, actionsRes, conditionsRes, statusesRes, storesRes, templatesRes, groupsRes, inpostRes, validationRes, orderFieldsRes, iconsRes] =
        await Promise.all([
          automationApi.getTriggers(),
          automationApi.getActions(),
          automationApi.getConditions(),
          api.get<Status[]>('/statuses'),
          api.get<Store[]>('/stores'),
          api.get<{ templates: EmailTemplate[] }>('/email/templates'),
          automationApi.getGroups(),
          automationApi.getInpostStatuses(),
          automationApi.getValidationErrors(),
          automationApi.getOrderFields(),
          automationApi.getButtonIcons(),
        ]);

      setTriggers(triggersRes.data.triggers);
      setActionsMeta(actionsRes.data.actions);
      setConditionsMeta(conditionsRes.data.conditions);
      setStatuses(statusesRes.data);
      setStores(storesRes.data);
      setTemplates(templatesRes.data.templates || []);
      setGroups(groupsRes.data.groups);
      setInpostStatuses(inpostRes.data.statuses);
      setValidationErrors(validationRes.data.errors);
      setOrderFields(orderFieldsRes.data);
      setAvailableIcons(iconsRes.data.icons);

      // Check if editing existing automation
      const pathParts = window.location.pathname.split('/');
      const id = pathParts[pathParts.length - 1];
      if (id && id !== 'new') {
        setAutomationId(id);
        await loadAutomation(id);
      }
    } catch (err) {
      console.error('Failed to load metadata:', err);
      setError('Nie udało się załadować danych');
    } finally {
      setLoading(false);
    }
  };

  const loadAutomation = async (id: string) => {
    try {
      const { data } = await automationApi.getOne(id);
      setName(data.name);
      setDescription(data.description || '');
      setIsActive(data.isActive);
      setPriority(data.priority);
      setGroupId(data.groupId || null);

      // Load triggers - support both array (new) and single object (legacy)
      if (Array.isArray(data.trigger)) {
        setSelectedTriggers(data.trigger.map((t: { type: TriggerType; config?: Record<string, unknown> }) => t.type));
        const configs: Record<string, Record<string, unknown>> = {};
        data.trigger.forEach((t: { type: TriggerType; config?: Record<string, unknown> }) => {
          configs[t.type] = t.config || {};
        });
        setTriggerConfigs(configs);
      } else {
        setSelectedTriggers([data.trigger.type]);
        setTriggerConfigs({ [data.trigger.type]: data.trigger.config || {} });
      }

      // Load trigger mode settings
      setTriggerMode(data.triggerMode || 'automatic');
      setButtonIcon(data.buttonIcon || 'Play');
      setButtonText(data.buttonText || '');
      setVisibleInStores(data.visibleInStores || []);

      // Parse conditions - check if wrapped in a single OR group
      const loadedConditions = data.conditions || [];
      if (
        loadedConditions.length === 1 &&
        isConditionGroup(loadedConditions[0]) &&
        loadedConditions[0].logic === 'OR'
      ) {
        // Unwrap OR group for UI
        setConditionsLogic('OR');
        setConditions(loadedConditions[0].conditions);
      } else {
        setConditionsLogic('AND');
        setConditions(loadedConditions);
      }

      setActions(data.actions.map((a) => ({
        type: a.type,
        config: a.config || {},
        continueOnError: a.continueOnError || false,
      })));
    } catch (err) {
      console.error('Failed to load automation:', err);
      setError('Nie udało się załadować automatyzacji');
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Nazwa jest wymagana');
      return;
    }
    // Skip trigger validation for manual mode - trigger is not required
    if (triggerMode === 'automatic' && selectedTriggers.length === 0) {
      setError('Wybierz co najmniej jeden trigger');
      return;
    }
    if (actions.length === 0) {
      setError('Dodaj co najmniej jedną akcję');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Prepare conditions - wrap in OR group if needed
      let finalConditions: ConditionItem[] = conditions;
      if (conditionsLogic === 'OR' && conditions.length > 0) {
        // Wrap all conditions in a single OR group for backend
        finalConditions = [{
          logic: 'OR' as const,
          conditions: conditions,
        }];
      }

      // Build trigger(s) - use array format for multiple triggers (OR logic)
      const buildTriggers = () => {
        if (triggerMode === 'manual') {
          return [{ type: 'manual.trigger' as const, config: {} }];
        }
        return selectedTriggers.map(type => ({
          type,
          config: triggerConfigs[type] || {}
        }));
      };

      const payload = {
        name,
        description: description || undefined,
        groupId,
        // Always use array format for triggers (backwards compatible - backend handles both)
        trigger: buildTriggers(),
        conditions: finalConditions,
        actions,
        isActive,
        priority,
        triggerMode,
        buttonIcon: triggerMode === 'manual' ? buttonIcon : null,
        buttonText: triggerMode === 'manual' ? (buttonText || null) : null,
        visibleInStores: triggerMode === 'manual' ? (visibleInStores.length > 0 ? visibleInStores : null) : null,
      };

      if (automationId) {
        await automationApi.update(automationId, payload);
      } else {
        await automationApi.create(payload);
      }

      navigate('/automations');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      setError(e.response?.data?.error || 'Wystąpił błąd podczas zapisywania');
    } finally {
      setSaving(false);
    }
  };

  const addAction = (type: ActionType) => {
    setActions([...actions, { type, config: {}, continueOnError: false }]);
  };

  const removeAction = (index: number) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  const updateAction = (index: number, updates: Partial<typeof actions[0]>) => {
    setActions(actions.map((a, i) => (i === index ? { ...a, ...updates } : a)));
  };

  const addCondition = () => {
    setConditions([...conditions, { field: '', operator: 'equals', value: '' }]);
  };

  const addConditionGroup = () => {
    setConditions([...conditions, {
      logic: 'OR',
      conditions: [{ field: '', operator: 'equals', value: '' }],
    }]);
  };

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index: number, updates: Partial<SimpleCondition>) => {
    setConditions(conditions.map((c, i) => {
      if (i !== index) return c;
      if (isConditionGroup(c)) return c; // Don't update groups with simple condition updates
      return { ...c, ...updates };
    }));
  };

  const updateConditionGroup = (index: number, updates: Partial<ConditionGroup>) => {
    setConditions(conditions.map((c, i) => {
      if (i !== index) return c;
      if (!isConditionGroup(c)) return c;
      return { ...c, ...updates };
    }));
  };

  const addConditionToGroup = (groupIndex: number) => {
    setConditions(conditions.map((c, i) => {
      if (i !== groupIndex || !isConditionGroup(c)) return c;
      return {
        ...c,
        conditions: [...c.conditions, { field: '', operator: 'equals', value: '' }],
      };
    }));
  };

  const removeConditionFromGroup = (groupIndex: number, conditionIndex: number) => {
    setConditions(conditions.map((c, i) => {
      if (i !== groupIndex || !isConditionGroup(c)) return c;
      const newConditions = c.conditions.filter((_, ci) => ci !== conditionIndex);
      // If group becomes empty, remove the whole group
      if (newConditions.length === 0) return null;
      return { ...c, conditions: newConditions };
    }).filter((c): c is ConditionItem => c !== null));
  };

  const updateConditionInGroup = (groupIndex: number, conditionIndex: number, updates: Partial<SimpleCondition>) => {
    setConditions(conditions.map((c, i) => {
      if (i !== groupIndex || !isConditionGroup(c)) return c;
      return {
        ...c,
        conditions: c.conditions.map((cond, ci) => {
          if (ci !== conditionIndex) return cond;
          if (isConditionGroup(cond)) return cond;
          return { ...cond, ...updates };
        }),
      };
    }));
  };

  // Get metadata for all selected triggers
  const selectedTriggersMeta = selectedTriggers
    .map(type => triggers.find(t => t.type === type))
    .filter((meta): meta is TriggerMeta => meta !== undefined);

  // Helper to render condition value input based on field type
  const renderConditionValueInput = (
    condition: SimpleCondition,
    conditionMeta: ConditionMeta | undefined,
    onUpdate: (updates: Partial<SimpleCondition>) => void
  ) => {
    if (condition.field === 'status') {
      return (
        <div className="flex-1">
          <StatusTreeSelect
            value={condition.value as string}
            onChange={(value) => onUpdate({ value })}
            statuses={statuses}
            placeholder="-- Wybierz status --"
          />
        </div>
      );
    }
    if (condition.field === 'store') {
      return (
        <div className="flex-1">
          <Select
            value={condition.value as string}
            onChange={(e) => onUpdate({ value: e.target.value })}
          >
            <option value="">-- Wybierz sklep --</option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
        </div>
      );
    }
    if (condition.field === 'payment_method') {
      const groups = orderFields.paymentMethodsByStore;
      return (
        <div className="flex-1">
          <GroupedSelect
            value={condition.value as string}
            onChange={(e) => onUpdate({ value: e.target.value })}
          >
            <option value="">-- Wybierz metodę płatności --</option>
            {groups.length > 0
              ? groups.map((g) => (
                  <optgroup key={g.storeId ?? '__nostore__'} label={g.storeName.toUpperCase()}>
                    {g.methods.map((m) => (
                      <option key={`${g.storeId}-${m}`} value={m}>{m}</option>
                    ))}
                  </optgroup>
                ))
              : orderFields.paymentMethods.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
          </GroupedSelect>
        </div>
      );
    }
    if (condition.field === 'shipping_method') {
      const groups = orderFields.shippingMethodsByStore;
      return (
        <div className="flex-1">
          <GroupedSelect
            value={condition.value as string}
            onChange={(e) => onUpdate({ value: e.target.value })}
          >
            <option value="">-- Wybierz metodę wysyłki --</option>
            {groups.length > 0
              ? groups.map((g) => (
                  <optgroup key={g.storeId ?? '__nostore__'} label={g.storeName.toUpperCase()}>
                    {g.methods.map((m) => (
                      <option key={`${g.storeId}-${m}`} value={m}>{m}</option>
                    ))}
                  </optgroup>
                ))
              : orderFields.shippingMethods.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
          </GroupedSelect>
        </div>
      );
    }
    if (condition.field === 'shipment_status') {
      return (
        <div className="flex-1">
          <Select
            value={condition.value as string}
            onChange={(e) => onUpdate({ value: e.target.value })}
          >
            <option value="">-- Wybierz status przesyłki --</option>
            {inpostStatuses.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </Select>
        </div>
      );
    }
    if (condition.field === 'shipment_error_type') {
      return (
        <div className="flex-1">
          <Select
            value={condition.value as string}
            onChange={(e) => onUpdate({ value: e.target.value })}
          >
            <option value="">-- Wybierz typ błędu --</option>
            {validationErrors.map((e) => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </Select>
        </div>
      );
    }
    if (condition.field === 'tag') {
      return (
        <div className="flex-1">
          <Select
            value={condition.value as string}
            onChange={(e) => onUpdate({ value: e.target.value })}
          >
            <option value="">-- Wybierz tag --</option>
            {orderFields.tags.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Select>
        </div>
      );
    }
    if (conditionMeta?.type === 'number') {
      return (
        <input
          type="number"
          value={condition.value as number}
          onChange={(e) => onUpdate({ value: parseFloat(e.target.value) || 0 })}
          className="input flex-1"
          placeholder="Wartość"
        />
      );
    }
    return (
      <input
        type="text"
        value={condition.value as string}
        onChange={(e) => onUpdate({ value: e.target.value })}
        className="input flex-1"
        placeholder="Wartość"
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">
            {automationId ? 'Edytuj automatyzację' : 'Nowa automatyzacja'}
          </h1>
          <p className="text-text-secondary mt-2">
            Skonfiguruj trigger, warunki i akcje
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger">
          <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto p-1 hover:bg-danger/20 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Basic Info */}
      <div className="card animate-slide-up">
        <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          <Info className="w-5 h-5 text-accent-primary" />
          Informacje podstawowe
        </h2>
        <div className="space-y-4">
          <div>
            <label className="label">Nazwa automatyzacji *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="np. Wyślij email po utworzeniu przesyłki"
            />
          </div>
          <div>
            <label className="label">Opis (opcjonalnie)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input"
              rows={2}
              placeholder="Krótki opis działania automatyzacji..."
            />
          </div>
          <div>
            <label className="label">Grupa (opcjonalnie)</label>
            <Select
              value={groupId || ''}
              onChange={(e) => setGroupId(e.target.value || null)}
            >
              <option value="">-- Bez grupy --</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-5 h-5 rounded border-border bg-surface-hover text-success focus:ring-success"
              />
              <span className="text-text-primary">Aktywna</span>
            </label>
            <div className="flex items-center gap-2">
              <label className="text-text-secondary text-sm">Priorytet:</label>
              <input
                type="number"
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
                className="input w-20 text-center"
                min={0}
                max={100}
              />
              <span title="Wyższy priorytet = wykonanie wcześniej">
                <HelpCircle className="w-4 h-4 text-text-muted" />
              </span>
            </div>
          </div>

          {/* Trigger Mode */}
          <div className="mt-4 pt-4 border-t border-border">
            <label className="label flex items-center gap-2">
              Sposób wywoływania
              <span title="Automatyczny = wywołuje się sama, Ręczny = przycisk w szczegółach zamówienia">
                <HelpCircle className="w-4 h-4 text-text-muted" />
              </span>
            </label>
            <div className="flex gap-4 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="triggerMode"
                  value="automatic"
                  checked={triggerMode === 'automatic'}
                  onChange={() => setTriggerMode('automatic')}
                  className="w-4 h-4 text-accent-primary bg-surface-hover border-border focus:ring-accent-primary"
                />
                <span className="text-text-primary">Automatyczny</span>
                <span className="text-xs text-text-muted">(domyślny)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="triggerMode"
                  value="manual"
                  checked={triggerMode === 'manual'}
                  onChange={() => setTriggerMode('manual')}
                  className="w-4 h-4 text-accent-primary bg-surface-hover border-border focus:ring-accent-primary"
                />
                <span className="text-text-primary">Ręczny</span>
                <span className="text-xs text-text-muted">(przycisk w zamówieniu)</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Trigger Selection */}
      <div className="card animate-slide-up" style={{ animationDelay: '0.1s' }}>
        <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          {triggerMode === 'manual' ? (
            <LucideIcons.MousePointerClick className="w-5 h-5 text-accent-secondary" />
          ) : (
            <Zap className="w-5 h-5 text-accent-secondary" />
          )}
          {triggerMode === 'manual' ? 'Trigger: Ręczne wywołanie' : 'Trigger (wyzwalacz) *'}
        </h2>

        {/* Manual mode: show button configuration */}
        {triggerMode === 'manual' ? (
          <div className="space-y-6">
            <p className="text-sm text-text-secondary">
              Ta automatyzacja będzie wyświetlana jako przycisk w sekcji "Własne akcje" w szczegółach zamówienia.
              Kliknięcie przycisku uruchomi akcje zdefiniowane poniżej.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label">Tekst przycisku</label>
                <input
                  type="text"
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value)}
                  className="input"
                  placeholder={name || 'Wykonaj akcję'}
                />
                <p className="text-xs text-text-muted mt-1">
                  Zostaw puste aby użyć nazwy automatyzacji
                </p>
              </div>

              <div>
                <label className="label">Ikona przycisku</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowIconPicker(!showIconPicker)}
                    className="flex items-center gap-2 px-4 py-2 bg-surface-muted rounded-lg border border-dark-500 hover:border-accent-primary transition-colors"
                  >
                    {(() => {
                      const IconComponent = (LucideIcons as unknown as Record<string, React.ElementType>)[buttonIcon] || LucideIcons.Play;
                      return <IconComponent className="w-5 h-5 text-accent-primary" />;
                    })()}
                    <span className="text-text-primary">{buttonIcon}</span>
                    <ChevronDown className="w-4 h-4 text-text-secondary" />
                  </button>
                </div>
              </div>
            </div>

            {/* Icon Picker */}
            {showIconPicker && (
              <div className="p-4 bg-surface-hover rounded-lg border border-border max-h-64 overflow-y-auto">
                <div className="grid grid-cols-8 gap-2">
                  {availableIcons.map((iconName) => {
                    const IconComponent = (LucideIcons as unknown as Record<string, React.ElementType>)[iconName];
                    if (!IconComponent) return null;
                    return (
                      <button
                        key={iconName}
                        type="button"
                        onClick={() => {
                          setButtonIcon(iconName);
                          setShowIconPicker(false);
                        }}
                        className={`p-2 rounded-lg flex flex-col items-center gap-1 transition-colors ${
                          buttonIcon === iconName
                            ? 'bg-accent-primary/20 border border-accent-primary'
                            : 'bg-surface-muted hover:bg-surface-overlay border border-transparent'
                        }`}
                        title={iconName}
                      >
                        <IconComponent className={`w-5 h-5 ${buttonIcon === iconName ? 'text-accent-primary' : 'text-text-secondary'}`} />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Store visibility selector */}
            <div className="mt-6">
              <label className="label">Widoczność w sklepach</label>
              <p className="text-xs text-text-muted mb-3">
                Wybierz sklepy, w których ten przycisk będzie widoczny. Zostaw puste, aby wyświetlać we wszystkich sklepach.
              </p>
              <div className="space-y-2 p-3 bg-surface-hover/50 rounded-lg border border-border max-h-48 overflow-y-auto">
                {stores.length === 0 ? (
                  <EmptyState
                    icon={LucideIcons.Store as React.ElementType}
                    title="Brak sklepów"
                    size="sm"
                  />
                ) : (
                  stores.map((store) => (
                    <label
                      key={store.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-surface-muted cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={visibleInStores.includes(store.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setVisibleInStores([...visibleInStores, store.id]);
                          } else {
                            setVisibleInStores(visibleInStores.filter((id) => id !== store.id));
                          }
                        }}
                        className="w-4 h-4 rounded border-dark-500 bg-surface-hover text-accent-primary focus:ring-accent-primary/50"
                      />
                      <span className="text-text-primary text-sm">{store.name}</span>
                      <span className="text-xs text-text-muted">({store.platform})</span>
                    </label>
                  ))
                )}
              </div>
              {visibleInStores.length > 0 && (
                <p className="text-xs text-text-secondary mt-2">
                  Wybrano: {visibleInStores.length} {visibleInStores.length === 1 ? 'sklep' : visibleInStores.length < 5 ? 'sklepy' : 'sklepów'}
                </p>
              )}
            </div>

            {/* Preview */}
            <div className="p-4 bg-surface-hover/50 rounded-lg border border-border mt-6">
              <p className="text-xs text-text-muted mb-2">Podgląd przycisku:</p>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-2 bg-surface-hover hover:bg-surface-muted border border-border hover:border-accent-primary/50 rounded-lg transition-colors"
              >
                {(() => {
                  const IconComponent = (LucideIcons as unknown as Record<string, React.ElementType>)[buttonIcon] || LucideIcons.Play;
                  return <IconComponent className="w-4 h-4 text-accent-primary" />;
                })()}
                <span className="text-text-primary text-sm">{buttonText || name || 'Wykonaj akcję'}</span>
              </button>
            </div>
          </div>
        ) : (
          /* Automatic mode: show trigger selection - multi-select with OR logic */
          <>
          <p className="text-sm text-text-muted mb-3">
            Wybierz jeden lub więcej triggerów. Automatyzacja uruchomi się gdy wystąpi KTÓRYKOLWIEK z wybranych triggerów.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {triggers.filter(t => t.type !== 'manual.trigger').map((trigger) => {
            const Icon = triggerIcons[trigger.type] || Zap;
            const isSelected = selectedTriggers.includes(trigger.type);
            return (
              <button
                key={trigger.type}
                onClick={() => {
                  // Toggle selection (multi-select)
                  if (isSelected) {
                    setSelectedTriggers(selectedTriggers.filter(t => t !== trigger.type));
                    // Remove config for this trigger
                    const newConfigs = { ...triggerConfigs };
                    delete newConfigs[trigger.type];
                    setTriggerConfigs(newConfigs);
                  } else {
                    setSelectedTriggers([...selectedTriggers, trigger.type]);
                    // Initialize empty config for this trigger
                    setTriggerConfigs({ ...triggerConfigs, [trigger.type]: {} });
                  }
                }}
                className={`p-4 rounded-xl border text-left transition-all relative ${
                  isSelected
                    ? 'border-accent-primary bg-accent-primary/10'
                    : 'border-border hover:border-dark-500 bg-surface-hover/50'
                }`}
              >
                {/* Selection indicator - always green for consistency.
                    V zawsze biały — bg-success jest solidnym ciemnym zielonym
                    w obu trybach, więc text-text-primary (ciemny w light mode)
                    znikał na zielonym tle. */}
                {isSelected && (
                  <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-success flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                <div className="flex items-center gap-3 mb-2">
                  <Icon className={`w-5 h-5 ${isSelected ? 'text-accent-primary' : 'text-text-secondary'}`} />
                  <span className={`font-medium ${isSelected ? 'text-text-primary' : 'text-text-secondary'}`}>
                    {trigger.name}
                  </span>
                </div>
                <p className="text-sm text-text-muted">{trigger.description}</p>
              </button>
            );
          })}
        </div>

        {/* Trigger Config - show config for each selected trigger */}
        {selectedTriggersMeta.filter(meta => meta.configFields.length > 0).map((triggerMeta) => {
          const triggerType = triggerMeta.type;
          const currentConfig = triggerConfigs[triggerType] || {};
          const Icon = triggerIcons[triggerType] || Zap;

          const updateConfig = (fieldName: string, value: unknown) => {
            setTriggerConfigs({
              ...triggerConfigs,
              [triggerType]: { ...currentConfig, [fieldName]: value }
            });
          };

          return (
            <div key={triggerType} className="mt-6 p-4 bg-surface-hover/50 rounded-xl border border-border">
              <h3 className="text-sm font-medium text-text-secondary mb-3 flex items-center gap-2">
                <Icon className="w-4 h-4 text-accent-primary" />
                Konfiguracja: {triggerMeta.name}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {triggerMeta.configFields.map((field) => (
                  <div key={field.name}>
                    <label className="label text-sm">{field.label}</label>
                    {field.name === 'from_status_id' || field.name === 'to_status_id' || field.name === 'status_id' ? (
                      <StatusTreeSelect
                        value={(currentConfig[field.name] as string) || ''}
                        onChange={(value) => updateConfig(field.name, value || undefined)}
                        statuses={statuses}
                        placeholder="-- Wybierz status --"
                      />
                    ) : field.name === 'status' && field.type === 'multi-select' ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto p-2 bg-surface-raised rounded-lg border border-border">
                        {inpostStatuses.map((s) => {
                          const selectedStatuses = Array.isArray(currentConfig[field.name])
                            ? (currentConfig[field.name] as string[])
                            : currentConfig[field.name]
                            ? [currentConfig[field.name] as string]
                            : [];
                          const isChecked = selectedStatuses.includes(s.value);
                          return (
                            <label key={s.value} className="flex items-center gap-2 cursor-pointer hover:bg-surface-hover p-1 rounded">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  const newValues = e.target.checked
                                    ? [...selectedStatuses, s.value]
                                    : selectedStatuses.filter((v) => v !== s.value);
                                  updateConfig(field.name, newValues.length > 0 ? newValues : undefined);
                                }}
                                className="rounded border-dark-500 bg-surface-hover text-accent-primary focus:ring-accent-primary"
                              />
                              <span className="text-sm text-text-secondary">{s.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    ) : field.name === 'error_type' && field.type === 'multi-select' ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto p-2 bg-surface-raised rounded-lg border border-border">
                        {validationErrors.map((e) => {
                          const selectedErrors = Array.isArray(currentConfig[field.name])
                            ? (currentConfig[field.name] as string[])
                            : currentConfig[field.name]
                            ? [currentConfig[field.name] as string]
                            : [];
                          const isChecked = selectedErrors.includes(e.value);
                          return (
                            <label key={e.value} className="flex items-center gap-2 cursor-pointer hover:bg-surface-hover p-1 rounded">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(ev) => {
                                  const newValues = ev.target.checked
                                    ? [...selectedErrors, e.value]
                                    : selectedErrors.filter((v) => v !== e.value);
                                  updateConfig(field.name, newValues.length > 0 ? newValues : undefined);
                                }}
                                className="rounded border-dark-500 bg-surface-hover text-accent-primary focus:ring-accent-primary"
                              />
                              <span className="text-sm text-text-secondary">{e.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    ) : field.type === 'select' ? (
                      <Select
                        value={(currentConfig[field.name] as string) || ''}
                        onChange={(e) => updateConfig(field.name, e.target.value || undefined)}
                      >
                        <option value="">-- Wybierz --</option>
                        {field.name === 'store_id' &&
                          stores.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        {field.options?.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Select>
                    ) : field.type === 'time' ? (
                      <input
                        type="time"
                        value={(currentConfig[field.name] as string) || ''}
                        onChange={(e) => updateConfig(field.name, e.target.value)}
                        className="input"
                      />
                    ) : (
                      <input
                        type={field.type === 'number' ? 'number' : 'text'}
                        value={(currentConfig[field.name] as string) || ''}
                        onChange={(e) => updateConfig(field.name, e.target.value)}
                        className="input"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
          </>
        )}
      </div>

      {/* Conditions */}
      <div className="card animate-slide-up" style={{ animationDelay: '0.2s' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-neon-orange" />
            Warunki (opcjonalne)
          </h2>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={addCondition} leftIcon={<Plus />}>
              Warunek
            </Button>
            <Button variant="secondary" size="sm" onClick={addConditionGroup} leftIcon={<FolderPlus />}>
              Grupa warunków
            </Button>
          </div>
        </div>

        {/* Logic selector - show only when there are conditions */}
        {conditions.length > 1 && (
          <div className="flex items-center gap-3 mb-4 p-3 bg-surface-hover/30 rounded-lg">
            <span className="text-sm text-text-secondary">Logika łączenia warunków:</span>
            <div className="flex items-center gap-1 bg-surface-raised rounded-lg p-1">
              <button
                onClick={() => setConditionsLogic('AND')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  conditionsLogic === 'AND'
                    ? 'bg-warning/20 text-warning'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                ORAZ (AND)
              </button>
              <button
                onClick={() => setConditionsLogic('OR')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  conditionsLogic === 'OR'
                    ? 'bg-accent-primary/20 text-accent-primary'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                LUB (OR)
              </button>
            </div>
            <span className="text-xs text-text-muted">
              {conditionsLogic === 'AND'
                ? '— wszystkie warunki muszą być spełnione'
                : '— wystarczy jeden spełniony warunek'}
            </span>
          </div>
        )}

        {conditions.length === 0 ? (
          <p className="text-text-muted text-sm">
            Automatyzacja będzie uruchamiana dla każdego zamówienia spełniającego trigger.
            Dodaj warunki, aby ograniczyć zakres działania.
          </p>
        ) : (
          <div className="space-y-3">
            {conditions.map((conditionItem, index) => {
              // Check if this is a condition group
              if (isConditionGroup(conditionItem)) {
                return (
                  <div key={index} className="p-4 bg-surface-hover/30 rounded-xl border border-border">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Folder className="w-4 h-4 text-accent-secondary" />
                        <span className="text-sm font-medium text-text-primary">Grupa warunków</span>
                        <div className="flex items-center gap-1 bg-surface-raised rounded-lg p-0.5">
                          <button
                            onClick={() => updateConditionGroup(index, { logic: 'AND' })}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              conditionItem.logic === 'AND'
                                ? 'bg-warning/20 text-warning'
                                : 'text-text-secondary hover:text-text-primary'
                            }`}
                          >
                            ORAZ
                          </button>
                          <button
                            onClick={() => updateConditionGroup(index, { logic: 'OR' })}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              conditionItem.logic === 'OR'
                                ? 'bg-accent-primary/20 text-accent-primary'
                                : 'text-text-secondary hover:text-text-primary'
                            }`}
                          >
                            LUB
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => addConditionToGroup(index)}
                          className="p-1.5 text-text-secondary hover:text-accent-primary transition-colors"
                          title="Dodaj warunek do grupy"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeCondition(index)}
                          className="p-1.5 text-danger hover:bg-danger/10 rounded"
                          title="Usuń grupę"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2 pl-4 border-l-2 border-border">
                      {conditionItem.conditions.map((groupCond, condIndex) => {
                        if (isConditionGroup(groupCond)) return null; // Nested groups not supported in UI
                        const condition = groupCond as SimpleCondition;
                        const conditionMeta = conditionsMeta.find((c) => c.field === condition.field);
                        const isRecentOrderCondition = condition.field === 'has_recent_order_from_customer';

                        return (
                          <div key={condIndex} className="p-2 bg-surface-raised/50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <Select
                                  value={condition.field}
                                  onChange={(e) => {
                                    const newField = e.target.value;
                                    const newMeta = conditionsMeta.find((c) => c.field === newField);
                                    updateConditionInGroup(index, condIndex, {
                                      field: newField,
                                      operator: newMeta?.operators[0] || 'equals',
                                      value: newField === 'has_recent_order_from_customer' ? 24 : '',
                                    });
                                  }}
                                  className="text-sm"
                                >
                                  <option value="">-- Pole --</option>
                                  {conditionsMeta.map((c) => (
                                    <option key={c.field} value={c.field}>
                                      {c.label}
                                    </option>
                                  ))}
                                </Select>
                              </div>
                              <div className="w-36">
                                <Select
                                  value={condition.operator}
                                  onChange={(e) => updateConditionInGroup(index, condIndex, { operator: e.target.value })}
                                  className="text-sm"
                                >
                                  {conditionMeta?.operators.map((op) => (
                                    <option key={op} value={op}>
                                      {getOperatorLabel(op)}
                                    </option>
                                  )) || <option value="equals">równa się</option>}
                                </Select>
                              </div>
                              {!['is_empty', 'is_not_empty', 'true', 'false'].includes(condition.operator) &&
                                !isRecentOrderCondition &&
                                renderConditionValueInput(condition, conditionMeta, (updates) =>
                                  updateConditionInGroup(index, condIndex, updates)
                                )}
                              <button
                                onClick={() => removeConditionFromGroup(index, condIndex)}
                                className="p-1.5 text-danger hover:bg-danger/10 rounded"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            {condition.field === 'all_product_sku' && (
                              <p className="mt-2 text-xs text-text-muted">
                                Każda pozycja musi mieć SKU i spełniać ten warunek. Wielkość liter nie ma znaczenia.
                              </p>
                            )}
                            {isRecentOrderCondition && (
                              <div className="mt-2 flex items-center gap-2 text-xs">
                                <span className="text-text-secondary">Ostatnie:</span>
                                <input
                                  type="number"
                                  value={(condition.value as number) || 1440}
                                  onChange={(e) =>
                                    updateConditionInGroup(index, condIndex, { value: parseInt(e.target.value) || 1440 })
                                  }
                                  className="input w-20 text-center text-xs"
                                  min={1}
                                />
                                <span className="text-text-muted">minut</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              // Simple condition
              const condition = conditionItem as SimpleCondition;
              const conditionMeta = conditionsMeta.find((c) => c.field === condition.field);
              const isRecentOrderCondition = condition.field === 'has_recent_order_from_customer';

              return (
                <div key={index} className="p-3 bg-surface-hover/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <Select
                        value={condition.field}
                        onChange={(e) => {
                          const newField = e.target.value;
                          const newMeta = conditionsMeta.find((c) => c.field === newField);
                          updateCondition(index, {
                            field: newField,
                            operator: newMeta?.operators[0] || 'equals',
                            value: newField === 'has_recent_order_from_customer' ? 24 : '',
                          });
                        }}
                      >
                        <option value="">-- Pole --</option>
                        {conditionsMeta.map((c) => (
                          <option key={c.field} value={c.field}>
                            {c.label}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div className="w-40">
                      <Select
                        value={condition.operator}
                        onChange={(e) => updateCondition(index, { operator: e.target.value })}
                      >
                        {conditionMeta?.operators.map((op) => (
                          <option key={op} value={op}>
                            {getOperatorLabel(op)}
                          </option>
                        )) || <option value="equals">równa się</option>}
                      </Select>
                    </div>
                    {!['is_empty', 'is_not_empty', 'true', 'false'].includes(condition.operator) &&
                      !isRecentOrderCondition &&
                      renderConditionValueInput(condition, conditionMeta, (updates) =>
                        updateCondition(index, updates)
                      )}
                    <button
                      onClick={() => removeCondition(index)}
                      className="p-2 text-danger hover:bg-danger/10 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {condition.field === 'all_product_sku' && (
                    <p className="mt-3 text-xs text-text-muted">
                      Każda pozycja zamówienia musi mieć SKU i spełniać ten warunek. „Zawiera eyelider” wykluczy zamówienie z dodatkiem NovaSkin. Wielkość liter nie ma znaczenia.
                    </p>
                  )}
                  {/* Special UI for recent order condition */}
                  {isRecentOrderCondition && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-text-secondary">
                          Sprawdź zamówienia z ostatnich:
                        </label>
                        <input
                          type="number"
                          value={(condition.value as number) || 1440}
                          onChange={(e) =>
                            updateCondition(index, { value: parseInt(e.target.value) || 1440 })
                          }
                          className="input w-28 text-center"
                          min={1}
                          max={43200}
                        />
                        <span className="text-sm text-text-secondary">minut</span>
                        <span className="text-xs text-text-muted">(1440 = 24h)</span>
                      </div>
                      <p className="text-xs text-text-muted mt-2">
                        Jeśli klient (ten sam email lub telefon) złożył inne zamówienie w tym czasie,
                        warunek zostanie spełniony. Przykłady: 60 = 1h, 1440 = 24h, 10080 = 7 dni.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="card animate-slide-up" style={{ animationDelay: '0.3s' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
            <Play className="w-5 h-5 text-success" />
            Akcje *
          </h2>
        </div>

        {/* Existing actions */}
        {actions.length > 0 && (
          <div className="space-y-3 mb-6">
            {actions.map((action, index) => {
              const meta = actionsMeta.find((a) => a.type === action.type);
              const Icon = actionIcons[action.type] || Zap;
              return (
                <div key={index} className="p-4 bg-surface-hover/50 rounded-xl border border-border">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-text-muted text-sm">{index + 1}.</span>
                      <Icon className="w-5 h-5 text-success" />
                      <span className="font-medium text-text-primary">{meta?.name || action.type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 text-sm text-text-secondary">
                        <input
                          type="checkbox"
                          checked={action.continueOnError}
                          onChange={(e) =>
                            updateAction(index, { continueOnError: e.target.checked })
                          }
                          className="w-4 h-4 rounded"
                        />
                        Kontynuuj przy błędzie
                      </label>
                      <button
                        onClick={() => removeAction(index)}
                        className="p-2 text-danger hover:bg-danger/10 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Action config */}
                  <ActionConfigForm
                    action={action}
                    statuses={statuses}
                    templates={templates}
                    tags={orderFields.tags}
                    onUpdate={(config) => updateAction(index, { config })}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Add action buttons */}
        <div>
          <p className="text-sm text-text-secondary mb-3">Dodaj akcję:</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {actionsMeta.map((action) => {
              const Icon = actionIcons[action.type] || Zap;
              return (
                <button
                  key={action.type}
                  onClick={() => addAction(action.type)}
                  className="p-3 rounded-lg border border-border hover:border-success/50 hover:bg-success/5 text-left transition-all group"
                >
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-text-secondary group-hover:text-success" />
                    <span className="text-sm text-text-secondary group-hover:text-text-primary">{action.name}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer with Save/Cancel — Primary LEWO, Cancel obok (konwencja 2026-04-21) */}
      <div className="flex items-center gap-2 pt-4 border-t border-border-subtle">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving}
          loading={saving}
          leftIcon={saving ? undefined : <Save />}
        >
          {saving ? 'Zapisywanie...' : 'Zapisz'}
        </Button>
        <Button variant="secondary" onClick={() => navigate('/automations')}>
          Anuluj
        </Button>
      </div>
    </div>
  );
}

function ActionConfigForm({
  action,
  statuses,
  templates,
  tags,
  onUpdate,
}: {
  action: { type: ActionType; config: Record<string, unknown> };
  statuses: Status[];
  templates: EmailTemplate[];
  tags: string[];
  onUpdate: (config: Record<string, unknown>) => void;
}) {
  const config = action.config;

  switch (action.type) {
    case 'change_status':
      return (
        <div>
          <label className="label text-sm">Zmień na status</label>
          <StatusTreeSelect
            value={(config.status_id as string) || ''}
            onChange={(value) => onUpdate({ ...config, status_id: value })}
            statuses={statuses}
            placeholder="-- Wybierz status --"
          />
        </div>
      );

    case 'send_email':
      return (
        <div>
          <label className="label text-sm">Szablon email</label>
          <Select
            value={(config.template_id as string) || ''}
            onChange={(e) => onUpdate({ ...config, template_id: e.target.value })}
          >
            <option value="">-- Wybierz szablon --</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </div>
      );

    case 'create_invoice':
      return (
        <div>
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={(config.send_to_ksef as boolean) || false}
              onChange={(e) => onUpdate({ ...config, send_to_ksef: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            Wyślij do KSeF
          </label>
        </div>
      );

    case 'add_tag':
    case 'remove_tag':
      return (
        <div>
          <label className="label text-sm">Nazwa tagu</label>
          {tags.length > 0 ? (
            <div className="flex gap-2">
              <div className="flex-1">
                <Select
                  value={(config.tag_name as string) || ''}
                  onChange={(e) => onUpdate({ ...config, tag_name: e.target.value })}
                >
                  <option value="">-- Wybierz istniejący tag --</option>
                  {tags.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </div>
              <input
                type="text"
                value={(config.tag_name as string) || ''}
                onChange={(e) => onUpdate({ ...config, tag_name: e.target.value })}
                className="input flex-1"
                placeholder="lub wpisz nowy..."
              />
            </div>
          ) : (
            <input
              type="text"
              value={(config.tag_name as string) || ''}
              onChange={(e) => onUpdate({ ...config, tag_name: e.target.value })}
              className="input"
              placeholder="np. VIP, Pilne, Do weryfikacji"
            />
          )}
        </div>
      );

    case 'add_note':
      return (
        <div className="space-y-3">
          <div>
            <label className="label text-sm">Treść notatki</label>
            <textarea
              value={(config.note_text as string) || ''}
              onChange={(e) => onUpdate({ ...config, note_text: e.target.value })}
              className="input"
              rows={2}
              placeholder="Treść notatki..."
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={(config.is_customer_note as boolean) || false}
              onChange={(e) => onUpdate({ ...config, is_customer_note: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            Notatka dla klienta (widoczna w WooCommerce)
          </label>
        </div>
      );

    case 'webhook':
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="label text-sm">Metoda</label>
              <Select
                value={(config.method as string) || 'POST'}
                onChange={(e) => onUpdate({ ...config, method: e.target.value })}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
              </Select>
            </div>
            <div className="col-span-3">
              <label className="label text-sm">URL</label>
              <input
                type="url"
                value={(config.url as string) || ''}
                onChange={(e) => onUpdate({ ...config, url: e.target.value })}
                className="input"
                placeholder="https://..."
              />
            </div>
          </div>
          <div>
            <label className="label text-sm">Body (JSON) - opcjonalnie</label>
            <textarea
              value={(config.body as string) || ''}
              onChange={(e) => onUpdate({ ...config, body: e.target.value })}
              className="input font-mono text-sm"
              rows={3}
              placeholder='{"order_id": "{{order_id}}", "status": "{{status}}"}'
            />
          </div>
        </div>
      );

    case 'delay':
      return (
        <div>
          <label className="label text-sm">Opóźnienie (minuty)</label>
          <input
            type="number"
            value={(config.minutes as number) || 0}
            onChange={(e) => onUpdate({ ...config, minutes: parseInt(e.target.value) || 0 })}
            className="input w-32"
            min={1}
            max={10080}
          />
          <p className="text-xs text-text-muted mt-1">Max 7 dni (10080 minut)</p>
        </div>
      );

    case 'create_shipment':
      return (
        <div className="space-y-3">
          <div>
            <label className="label text-sm">Metoda wysyłki</label>
            <Select
              value={(config.method as string) || 'auto'}
              onChange={(e) => onUpdate({ ...config, method: e.target.value })}
            >
              <option value="auto">Automatycznie (z mapowania sklepu)</option>
              <option value="locker">Paczkomat</option>
              <option value="courier">Kurier</option>
            </Select>
            <p className="text-xs text-text-muted mt-1">
              {(config.method as string) === 'auto' || !config.method
                ? 'Typ przesyłki zostanie automatycznie wykryty na podstawie mapowania metod wysyłki w ustawieniach sklepu.'
                : 'Typ przesyłki ustawiony ręcznie - niezależnie od mapowania sklepu.'}
            </p>
          </div>
        </div>
      );

    default:
      return (
        <p className="text-sm text-text-muted">
          Ta akcja nie wymaga dodatkowej konfiguracji.
        </p>
      );
  }
}

function AutomationLogs() {
  const [logs, setLogs] = useState<AutomationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [automationId, setAutomationId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Get automation ID from URL
    const pathParts = window.location.pathname.split('/');
    const id = pathParts[pathParts.length - 2];
    if (id && id !== 'automations') {
      setAutomationId(id);
      loadLogs(id);
    }
  }, []);

  const loadLogs = async (id: string, page = 1) => {
    try {
      setLoading(true);
      const { data } = await automationApi.getLogs(id, { page, limit: 20 });
      setLogs(data.logs);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Failed to load logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    if (automationId) {
      loadLogs(automationId, page);
    }
  };

  return (
    <div className="space-y-8">
      <div className="animate-fade-in">
        <PageHeader
          icon={History}
          title="Historia wykonań"
          subtitle="Szczegółowa historia uruchomień automatyzacji"
          actions={
            <Button variant="secondary" onClick={() => navigate('/automations')}>
              Wróć do listy
            </Button>
          }
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="loader" />
        </div>
      ) : logs.length === 0 ? (
        <EmptyState
          icon={History}
          title="Brak logów"
          description="Ta automatyzacja nie była jeszcze uruchamiana"
          size="sm"
        />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-surface-hover">
              <tr>
                <th className="text-left p-4 text-text-secondary font-medium">Status</th>
                <th className="text-left p-4 text-text-secondary font-medium">Zamówienie</th>
                <th className="text-left p-4 text-text-secondary font-medium">Trigger</th>
                <th className="text-left p-4 text-text-secondary font-medium">Czas</th>
                <th className="text-left p-4 text-text-secondary font-medium">Data</th>
                <th className="text-left p-4 text-text-secondary font-medium">Błąd</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-t border-border hover:bg-surface-hover/50">
                  <td className="p-4">
                    <LogStatusBadge status={log.status} />
                  </td>
                  <td className="p-4">
                    {log.order ? (
                      <Link
                        to={`/orders?search=${encodeURIComponent(log.order.orderNumber)}`}
                        className="text-accent-primary hover:underline"
                      >
                        #{log.order.orderNumber}
                      </Link>
                    ) : (
                      <span className="text-text-muted">-</span>
                    )}
                  </td>
                  <td className="p-4 text-text-secondary">{log.triggerType}</td>
                  <td className="p-4 text-text-secondary">
                    {log.durationMs ? `${log.durationMs}ms` : '-'}
                  </td>
                  <td className="p-4 text-text-secondary">
                    {new Date(log.createdAt).toLocaleString('pl-PL')}
                  </td>
                  <td className="p-4 text-danger text-sm max-w-xs truncate" title={log.error || ''}>
                    {log.error || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-border">
              <p className="text-sm text-text-secondary">
                Strona {pagination.page} z {pagination.pages} (łącznie: {pagination.total})
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                >
                  Poprzednia
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                >
                  Następna
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AllLogsPage() {
  const [logs, setLogs] = useState<AutomationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [statusFilter, setStatusFilter] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    loadLogs();
  }, [statusFilter]);

  const loadLogs = async (page = 1) => {
    try {
      setLoading(true);
      const { data } = await automationApi.getAllLogs({
        page,
        limit: 20,
        status: statusFilter || undefined,
      });
      setLogs(data.logs);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Failed to load logs:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="animate-fade-in">
        <PageHeader
          icon={History}
          title="Wszystkie logi"
          subtitle="Historia wykonań wszystkich automatyzacji"
          actions={
            <Button variant="secondary" onClick={() => navigate('/automations')}>
              Wróć do listy
            </Button>
          }
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="w-48">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Wszystkie statusy</option>
            <option value="success">Sukces</option>
            <option value="failed">Błąd</option>
            <option value="skipped">Pominięto</option>
          </Select>
        </div>
        <Button variant="secondary" onClick={() => loadLogs()} leftIcon={<RefreshCw />}>
          Odśwież
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="loader" />
        </div>
      ) : logs.length === 0 ? (
        <EmptyState
          icon={History}
          title="Brak logów"
          description="Żadna automatyzacja nie była jeszcze uruchamiana"
          size="sm"
        />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-surface-hover">
              <tr>
                <th className="text-left p-4 text-text-secondary font-medium">Status</th>
                <th className="text-left p-4 text-text-secondary font-medium">Automatyzacja</th>
                <th className="text-left p-4 text-text-secondary font-medium">Zamówienie</th>
                <th className="text-left p-4 text-text-secondary font-medium">Trigger</th>
                <th className="text-left p-4 text-text-secondary font-medium">Czas</th>
                <th className="text-left p-4 text-text-secondary font-medium">Data</th>
                <th className="text-left p-4 text-text-secondary font-medium">Błąd</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-t border-border hover:bg-surface-hover/50">
                  <td className="p-4">
                    <LogStatusBadge status={log.status} />
                  </td>
                  <td className="p-4">
                    {log.automation ? (
                      <Link
                        to={`/automations/${log.automation.id}`}
                        className="text-text-primary hover:text-accent-primary"
                      >
                        {log.automation.name}
                      </Link>
                    ) : (
                      <span className="text-text-muted">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    {log.order ? (
                      <Link
                        to={`/orders?search=${encodeURIComponent(log.order.orderNumber)}`}
                        className="text-accent-primary hover:underline"
                      >
                        #{log.order.orderNumber}
                      </Link>
                    ) : (
                      <span className="text-text-muted">-</span>
                    )}
                  </td>
                  <td className="p-4 text-text-secondary">{log.triggerType}</td>
                  <td className="p-4 text-text-secondary">
                    {log.durationMs ? `${log.durationMs}ms` : '-'}
                  </td>
                  <td className="p-4 text-text-secondary">
                    {new Date(log.createdAt).toLocaleString('pl-PL')}
                  </td>
                  <td className="p-4 text-danger text-sm max-w-xs truncate" title={log.error || ''}>
                    {log.error || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-border">
              <p className="text-sm text-text-secondary">
                Strona {pagination.page} z {pagination.pages} (łącznie: {pagination.total})
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => loadLogs(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                >
                  Poprzednia
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => loadLogs(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                >
                  Następna
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LogStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'success':
      return (
        <span className="badge badge-success flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" />
          Sukces
        </span>
      );
    case 'failed':
      return (
        <span className="badge badge-error flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          Błąd
        </span>
      );
    case 'skipped':
      return (
        <span className="badge badge-warning flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Pominięto
        </span>
      );
    case 'processing':
      return (
        <span className="badge badge-info flex items-center gap-1">
          <RefreshCw className="w-3 h-3 animate-spin" />
          W toku
        </span>
      );
    default:
      return <span className="badge">{status}</span>;
  }
}
